import {
  BACKUP_STATUS_LABELS,
  type BackupEntry,
  type Page,
  PERMISSIONS,
  type Settings,
  type UpdateSettingsInput,
} from '@edms/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DatabaseBackup, Save } from 'lucide-react';
import { useEffect, useState } from 'react';

import { PageBody, PageHeader } from '@/components/page-header';
import { useToast } from '@/components/ui/overlays';
import { Button, Checkbox, Field, Input } from '@/components/ui/primitives';
import { Badge, Card, SectionHeader, Skeleton } from '@/components/ui/surfaces';
import { useAuth } from '@/features/auth/auth-context';
import { api, ApiError } from '@/lib/api-client';
import { formatBytes } from '@/lib/utils';

/**
 * Paramètres d'exploitation (§15, §16, §20.2).
 *
 * Chaque réglage porte une aide qui explique **l'effet métier**, pas la
 * mécanique : « en deçà, le document part en validation manuelle » est plus
 * utile à un administrateur que « seuil de confiance du moteur ». C'est ce qui
 * permet à un responsable de bureau d'ajuster le système sans documentation
 * externe.
 */
export function SettingsPage(): React.JSX.Element {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['settings'],
    queryFn: () => api.get<Settings>('/settings'),
  });

  const [draft, setDraft] = useState<Settings | null>(null);
  useEffect(() => {
    if (data) setDraft(data);
  }, [data]);

  const save = useMutation({
    mutationFn: (input: UpdateSettingsInput) => api.patch<Settings>('/settings', input),
    onSuccess: (settings) => {
      toast.success('Paramètres enregistrés');
      setDraft(settings);
      void queryClient.invalidateQueries({ queryKey: ['settings'] });
    },
    onError: (error) =>
      toast.error('Enregistrement refusé', error instanceof ApiError ? error.message : undefined),
  });

  const { data: backups } = useQuery({
    queryKey: ['backups'],
    queryFn: () => api.get<Page<BackupEntry>>('/backups', { page: 1, pageSize: 5 }),
    enabled: can(PERMISSIONS.BACKUPS_MANAGE),
  });

  const runBackup = useMutation({
    mutationFn: () => api.post<BackupEntry>('/backups/run'),
    onSuccess: (entry) => {
      toast.success(
        'Sauvegarde terminée',
        `${entry.label} · ${entry.documentCount} document(s) copié(s)`,
      );
      void queryClient.invalidateQueries({ queryKey: ['backups'] });
    },
    onError: (error) =>
      toast.error('Sauvegarde échouée', error instanceof ApiError ? error.message : undefined),
  });

  const writable = can(PERMISSIONS.SETTINGS_WRITE);
  const dirty = draft !== null && data !== undefined && JSON.stringify(draft) !== JSON.stringify(data);

  if (isLoading || !draft) {
    return (
      <PageBody>
        {[0, 1, 2].map((index) => (
          <Card key={index} className="space-y-3">
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-24 w-full" />
          </Card>
        ))}
      </PageBody>
    );
  }

  const patch = <K extends keyof Settings>(category: K, values: Partial<Settings[K]>): void => {
    setDraft((current) =>
      current ? { ...current, [category]: { ...current[category], ...values } } : current,
    );
  };

  return (
    <>
      <PageHeader
        title="Paramètres"
        description="Numérisation, reconnaissance, doublons, sécurité et stockage"
        actions={
          writable && (
            <Button
              variant="primary"
              icon={<Save className="size-4" />}
              disabled={!dirty}
              loading={save.isPending}
              onClick={() => save.mutate(draft)}
            >
              Enregistrer
            </Button>
          )
        }
      />

      <PageBody>
        <fieldset disabled={!writable} className="space-y-5">
          <Card>
            <SectionHeader
              title="Numérisation"
              description="Correction automatique des images et découpage des reçus"
            />
            <div className="mt-4 grid gap-4 lg:grid-cols-2">
              <div className="space-y-3">
                <Checkbox
                  label="Redressement automatique"
                  hint="Corrige l'inclinaison des pages engagées de travers dans le chargeur."
                  checked={draft.scanning.autoDeskew}
                  onChange={(event) => patch('scanning', { autoDeskew: event.target.checked })}
                />
                <Checkbox
                  label="Amélioration du contraste"
                  hint="Rend lisibles les reçus thermiques pâlis ; bénéficie directement à l'OCR."
                  checked={draft.scanning.autoContrast}
                  onChange={(event) => patch('scanning', { autoContrast: event.target.checked })}
                />
                <Checkbox
                  label="Suppression du bruit"
                  hint="Retire les points parasites du scanner sans émousser les chiffres."
                  checked={draft.scanning.denoise}
                  onChange={(event) => patch('scanning', { denoise: event.target.checked })}
                />
                <Checkbox
                  label="Suppression des pages blanches"
                  hint="Les versos vierges n'entrent pas dans les archives."
                  checked={draft.scanning.removeBlankPages}
                  onChange={(event) => patch('scanning', { removeBlankPages: event.target.checked })}
                />
                <Checkbox
                  label="Découper sur page blanche"
                  hint="Une feuille vierge intercalée sépare deux reçus."
                  checked={draft.scanning.splitOnBlankPage}
                  onChange={(event) => patch('scanning', { splitOnBlankPage: event.target.checked })}
                />
              </div>

              <div className="space-y-3">
                <Field
                  label="Résolution minimale (dpi)"
                  htmlFor="scan-dpi"
                  hint="Le scanner recommandé produit 300 dpi."
                >
                  <NumberInput
                    id="scan-dpi"
                    value={draft.scanning.minimumDpi}
                    min={150}
                    max={1200}
                    onChange={(value) => patch('scanning', { minimumDpi: value })}
                  />
                </Field>

                <Field
                  label="Pages par reçu"
                  htmlFor="scan-pages"
                  hint="Utilisé lorsque le découpage sur page blanche est désactivé."
                >
                  <NumberInput
                    id="scan-pages"
                    value={draft.scanning.pagesPerReceipt}
                    min={1}
                    max={20}
                    onChange={(value) => patch('scanning', { pagesPerReceipt: value })}
                  />
                </Field>

                <Field
                  label="Seuil de page blanche"
                  htmlFor="scan-blank"
                  hint="Proportion de pixels clairs au-delà de laquelle la page est jugée vierge."
                >
                  <NumberInput
                    id="scan-blank"
                    value={draft.scanning.blankPageThreshold}
                    min={0.9}
                    max={1}
                    step={0.005}
                    onChange={(value) => patch('scanning', { blankPageThreshold: value })}
                  />
                </Field>
              </div>
            </div>
          </Card>

          <Card>
            <SectionHeader
              title="Reconnaissance"
              description="Moteur OCR et seuil d'acceptation automatique"
            />
            <div className="mt-4 grid gap-4 lg:grid-cols-3">
              <Checkbox
                label="OCR activé"
                hint="Désactivé, tous les documents passent en saisie manuelle."
                checked={draft.ocr.enabled}
                onChange={(event) => patch('ocr', { enabled: event.target.checked })}
              />

              <Field
                label="Confiance minimale d'acceptation"
                htmlFor="ocr-confidence"
                hint="En deçà, le document part en validation manuelle. À 1, aucun document n'est archivé sans contrôle : la validation se fait lot par lot."
              >
                <NumberInput
                  id="ocr-confidence"
                  value={draft.ocr.autoAcceptConfidence}
                  min={0}
                  max={1}
                  step={0.01}
                  onChange={(value) => patch('ocr', { autoAcceptConfidence: value })}
                />
              </Field>

              <Field
                label="Traitements simultanés"
                htmlFor="ocr-concurrency"
                hint="Chaque traitement charge un modèle de langue en mémoire."
              >
                <NumberInput
                  id="ocr-concurrency"
                  value={draft.ocr.concurrency}
                  min={1}
                  max={16}
                  onChange={(value) => patch('ocr', { concurrency: value })}
                />
              </Field>
            </div>
          </Card>

          <Card>
            <SectionHeader
              title="Détection des doublons"
              description="Trois contrôles indépendants, du plus certain au plus heuristique"
            />
            <div className="mt-4 grid gap-4 lg:grid-cols-2">
              <div className="space-y-3">
                <Checkbox
                  label="Empreinte du fichier (SHA-256)"
                  hint="Le même fichier, bit pour bit. Aucun faux positif possible."
                  checked={draft.duplicates.checkFileHash}
                  onChange={(event) => patch('duplicates', { checkFileHash: event.target.checked })}
                />
                <Checkbox
                  label="Clé métier"
                  hint="Même numéro, sous-numéro, bureau et date de livraison."
                  checked={draft.duplicates.checkBusinessKey}
                  onChange={(event) => patch('duplicates', { checkBusinessKey: event.target.checked })}
                />
                <Checkbox
                  label="Ressemblance visuelle"
                  hint="Rapproche deux numérisations d'un même papier malgré rotation ou luminosité."
                  checked={draft.duplicates.checkPerceptualHash}
                  onChange={(event) =>
                    patch('duplicates', { checkPerceptualHash: event.target.checked })
                  }
                />
                <Checkbox
                  label="Bloquer l'archivage sur doublon"
                  hint="Le document attend un arbitrage humain avant d'entrer dans les archives."
                  checked={draft.duplicates.blockArchiveOnDuplicate}
                  onChange={(event) =>
                    patch('duplicates', { blockArchiveOnDuplicate: event.target.checked })
                  }
                />
              </div>

              <Field
                label="Tolérance de ressemblance"
                htmlFor="dup-distance"
                hint="Nombre de bits pouvant différer sur une empreinte de 256 bits. Plus la valeur est élevée, plus la détection est large."
              >
                <NumberInput
                  id="dup-distance"
                  value={draft.duplicates.perceptualDistanceThreshold}
                  min={0}
                  max={128}
                  onChange={(value) => patch('duplicates', { perceptualDistanceThreshold: value })}
                />
              </Field>
            </div>
          </Card>

          <Card>
            <SectionHeader title="Sécurité" description="Sessions et protection des comptes" />
            <div className="mt-4 grid gap-4 lg:grid-cols-3">
              <Field label="Durée de session (minutes)" htmlFor="sec-session">
                <NumberInput
                  id="sec-session"
                  value={draft.security.sessionMinutes}
                  min={5}
                  max={1440}
                  onChange={(value) => patch('security', { sessionMinutes: value })}
                />
              </Field>

              <Field
                label="Tentatives avant verrouillage"
                htmlFor="sec-attempts"
                hint="Protection contre les attaques par force brute."
              >
                <NumberInput
                  id="sec-attempts"
                  value={draft.security.maxFailedLogins}
                  min={3}
                  max={20}
                  onChange={(value) => patch('security', { maxFailedLogins: value })}
                />
              </Field>

              <Field label="Durée de verrouillage (minutes)" htmlFor="sec-lockout">
                <NumberInput
                  id="sec-lockout"
                  value={draft.security.lockoutMinutes}
                  min={1}
                  max={1440}
                  onChange={(value) => patch('security', { lockoutMinutes: value })}
                />
              </Field>
            </div>
          </Card>

          <Card>
            <SectionHeader
              title="Stockage et acquisition"
              description="Coffre documentaire et dossier surveillé par le scanner"
            />
            <div className="mt-4 grid gap-4 lg:grid-cols-2">
              <div className="space-y-3">
                <Field
                  label="Alerte espace disque (Go)"
                  htmlFor="storage-low"
                  hint="Une notification est émise sous ce seuil."
                >
                  <NumberInput
                    id="storage-low"
                    value={draft.storage.lowDiskWarningGb}
                    min={1}
                    max={10_000}
                    onChange={(value) => patch('storage', { lowDiskWarningGb: value })}
                  />
                </Field>

                <Field label="Durée de conservation (années)" htmlFor="storage-retention">
                  <NumberInput
                    id="storage-retention"
                    value={draft.storage.retentionYears}
                    min={1}
                    max={50}
                    onChange={(value) => patch('storage', { retentionYears: value })}
                  />
                </Field>
              </div>

              <div className="space-y-3">
                <Checkbox
                  label="Dossier surveillé activé"
                  hint="Le scanner y dépose ses fichiers, rattachés au lot ouvert du bureau."
                  checked={draft.ingestion.watchFolderEnabled}
                  onChange={(event) =>
                    patch('ingestion', { watchFolderEnabled: event.target.checked })
                  }
                />

                <Field
                  label="Chemin du dossier surveillé"
                  htmlFor="inbox-path"
                  hint="Un sous-dossier par code bureau. Redémarrage du service requis après modification."
                >
                  <Input
                    id="inbox-path"
                    value={draft.ingestion.watchFolderPath}
                    onChange={(event) => patch('ingestion', { watchFolderPath: event.target.value })}
                    className="font-mono"
                    placeholder="\\\\serveur\\scans"
                  />
                </Field>

                <Field
                  label="Délai de stabilité (ms)"
                  htmlFor="inbox-stability"
                  hint="Attente avant lecture, le temps que le scanner finisse d'écrire."
                >
                  <NumberInput
                    id="inbox-stability"
                    value={draft.ingestion.stabilityMs}
                    min={250}
                    max={60_000}
                    step={250}
                    onChange={(value) => patch('ingestion', { stabilityMs: value })}
                  />
                </Field>
              </div>
            </div>
          </Card>

          <Card>
            <SectionHeader
              title="Sauvegardes automatiques"
              description="Vidage de la base et copie incrémentale des documents archivés"
              actions={
                can(PERMISSIONS.BACKUPS_MANAGE) && (
                  <Button
                    size="sm"
                    icon={<DatabaseBackup className="size-3.5" />}
                    loading={runBackup.isPending}
                    onClick={() => runBackup.mutate()}
                  >
                    Sauvegarder maintenant
                  </Button>
                )
              }
            />
            <div className="mt-4 grid gap-4 lg:grid-cols-2">
              <div className="space-y-3">
                <Checkbox
                  label="Sauvegarde automatique activée"
                  hint="Une exécution par jour à l'heure choisie."
                  checked={draft.backup.enabled}
                  onChange={(event) => patch('backup', { enabled: event.target.checked })}
                />
                <Checkbox
                  label="Inclure les documents archivés"
                  hint="Copie uniquement les documents ajoutés depuis la dernière sauvegarde réussie."
                  checked={draft.backup.includeDocuments}
                  onChange={(event) => patch('backup', { includeDocuments: event.target.checked })}
                />

                <Field
                  label="Destination"
                  htmlFor="backup-destination"
                  hint="Un volume distinct de celui des données. La copie hors site reste à la charge de l'exploitation (règle 3-2-1)."
                >
                  <Input
                    id="backup-destination"
                    value={draft.backup.destination}
                    onChange={(event) => patch('backup', { destination: event.target.value })}
                    className="font-mono"
                  />
                </Field>
              </div>

              <div className="space-y-3">
                <Field label="Heure d'exécution" htmlFor="backup-hour" hint="Heure locale du serveur (0 à 23).">
                  <NumberInput
                    id="backup-hour"
                    value={draft.backup.hour}
                    min={0}
                    max={23}
                    onChange={(value) => patch('backup', { hour: value })}
                  />
                </Field>

                <Field
                  label="Conservation (jours)"
                  htmlFor="backup-retention"
                  hint="Au-delà, les sauvegardes locales sont supprimées."
                >
                  <NumberInput
                    id="backup-retention"
                    value={draft.backup.retentionDays}
                    min={1}
                    max={3650}
                    onChange={(value) => patch('backup', { retentionDays: value })}
                  />
                </Field>

                {backups && backups.items.length > 0 && (
                  <div className="rounded-md border border-line-subtle bg-sunken/50 px-3 py-2.5">
                    <p className="text-2xs font-semibold tracking-wide text-ink-faint uppercase">
                      Dernières exécutions
                    </p>
                    <ul className="mt-2 space-y-1.5">
                      {backups.items.slice(0, 3).map((entry) => (
                        <li key={entry.id} className="flex items-center justify-between gap-2 text-xs">
                          <span className="font-mono text-ink-muted">{entry.label}</span>
                          <span className="flex items-center gap-2">
                            <span className="text-ink-faint">
                              {formatBytes(entry.databaseBytes + entry.documentBytes)}
                            </span>
                            <Badge tone={entry.status === 'SUCCESS' ? 'success' : entry.status === 'FAILED' ? 'danger' : 'info'}>
                              {BACKUP_STATUS_LABELS[entry.status]}
                            </Badge>
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </Card>

          <Card>
            <SectionHeader
              title="Intelligence artificielle"
              description="Classification automatique et complétion des métadonnées (§20.2)"
            />
            <div className="mt-4 grid gap-4 lg:grid-cols-2">
              <div className="space-y-3">
                <Checkbox
                  label="Classification automatique du type de document"
                  checked={draft.ai.classificationEnabled}
                  onChange={(event) => patch('ai', { classificationEnabled: event.target.checked })}
                />
                <Checkbox
                  label="Compléter les métadonnées manquantes"
                  hint="Uniquement au-dessus du seuil de confiance ci-contre."
                  checked={draft.ai.autoFillMetadata}
                  onChange={(event) => patch('ai', { autoFillMetadata: event.target.checked })}
                />
                <Checkbox
                  label="Apprendre des corrections des opérateurs"
                  hint="Les corrections manuelles sont conservées comme jeu d'apprentissage."
                  checked={draft.ai.learnFromCorrections}
                  onChange={(event) => patch('ai', { learnFromCorrections: event.target.checked })}
                />
              </div>

              <Field label="Confiance minimale de l'IA" htmlFor="ai-confidence">
                <NumberInput
                  id="ai-confidence"
                  value={draft.ai.minimumConfidence}
                  min={0}
                  max={1}
                  step={0.01}
                  onChange={(value) => patch('ai', { minimumConfidence: value })}
                />
              </Field>
            </div>
          </Card>
        </fieldset>
      </PageBody>
    </>
  );
}

function NumberInput({
  id,
  value,
  min,
  max,
  step = 1,
  onChange,
}: {
  id: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  onChange: (value: number) => void;
}): React.JSX.Element {
  return (
    <Input
      id={id}
      type="number"
      value={value}
      min={min}
      max={max}
      step={step}
      onChange={(event) => {
        const parsed = Number(event.target.value);
        if (Number.isFinite(parsed)) onChange(parsed);
      }}
      className="tabular"
    />
  );
}
